import { useEffect, useRef } from 'react';

export const useFocusTrap = (containerRef, { onEscape, autoFocus = true } = {}) => {
    const onEscapeRef = useRef(onEscape);

    useEffect(() => {
        onEscapeRef.current = onEscape;
    }, [onEscape]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const previouslyFocused = document.activeElement;
        const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        if (autoFocus) {
            const firstFocusable = container.querySelector(focusableSelector);
            firstFocusable?.focus();
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && onEscapeRef.current) {
                e.stopPropagation();
                onEscapeRef.current();
                return;
            }
            if (e.key !== 'Tab') return;
            const focusableElements = container.querySelectorAll(focusableSelector);
            if (focusableElements.length === 0) return;
            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        container.addEventListener('keydown', handleKeyDown);
        return () => {
            container.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, [containerRef, autoFocus]);
};

export const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};